// Issue #126, Session 78h — the address side of a notification.
//
// Read the gate before changing anything here: docs/gates/Stede_EmailDeepLinks_Gate_126.html.
// Two findings in it decide the shape of this file, and neither is obvious from
// the issue text:
//
//   1. There was no URL to put in an email. `notificationNav.js` maps a
//      notification's `link_to` onto App-level React state setters — `setActive`,
//      `setSelectedItemId`, `setOpenRequestId`. `requests_inbox` is a page key,
//      not an address. Something had to translate one into the other, and this
//      is that something.
//
//   2. The login round-trip keeps `from.pathname` and throws the query string
//      away (`Login.js`, and `App.js` stores the whole location). A notification
//      deep link must require an already-authenticated user — it is not a magic
//      link — so the signed-out round-trip is the NORMAL path for an email
//      recipient, not an edge case. A `?query=` deep link therefore works for
//      anyone already signed in and silently loses its destination for everyone
//      else: it passes every test an authenticated developer runs. Hence a real
//      path, `/go/:linkTo/:linkId?`. Gate decision E1.
//
// The route is also a sibling of `/tool/:id` and `/consumable/:id`, so it adds
// no new pattern; a path is legible in a mail client's status bar before the
// click; and corporate link scanners rewrite query strings but not paths.

import { emailTargetUrl } from './appUrl';
import { isStaffWithPurchasing, canSeeWelding } from './permissions';

// Gate decision E10 — built from CANONICAL_APP_URL, never window.location.origin.
// An email is rendered by whoever triggered it and read by someone else, so the
// sender's origin would bake a preview-branch URL into real mail. Same rule as
// printed QR labels (#320), and it lives in appUrl.js with its reasoning.
// AMENDED at Session 79.7 for #507. E10's instinct was right and its remedy was
// wrong: "never window.location.origin" is correct for a PRINTED LABEL, which
// outlives every environment, and wrong for an EMAIL, which should open the
// copy of Stede that sent it. This function has exactly one kind of caller —
// all fourteen are `ctaUrl` in notify.js's email templates, checked rather than
// assumed — so it is unambiguously the email side of that split.
//
// The rule itself lives in `appUrl.js` beside `labelTargetUrl()`, deliberately,
// so the two opposite answers stay next to each other and the next person to
// change one reads the other.
export function deepLinkUrl(linkTo, linkId) {
  return emailTargetUrl(deepLinkPath(linkTo, linkId));
}

// Gate decision E2 — a page with no record is a legitimate destination. Counted
// at 78f: of 43 sendNotification() call sites all 43 pass link_to and 30 pass
// link_id, so the 13 that pass a page key alone must resolve to something better
// than the front door.
export function deepLinkPath(linkTo, linkId) {
  if (!linkTo || typeof linkTo !== 'string') return null;
  // A link_to that is already a path is handled by useNotificationNav's own
  // startsWith('/') branch and has no business being wrapped in /go/.
  if (linkTo.startsWith('/')) return linkTo;
  const page = encodeURIComponent(linkTo);
  if (linkId == null || linkId === '') return `/go/${page}`;
  return `/go/${page}/${encodeURIComponent(String(linkId))}`;
}

// Gate decision E7 — a destination the recipient's role cannot reach must name
// itself. "You don't have access" with no subject is a dead end; naming the page
// turns it into something the reader can report to an administrator. These are
// the words a person would use, not page keys.
export const PAGE_LABELS = {
  fleet: 'Fleet Inventory',
  tagout: 'Tag Out',
  spare: 'the Spare Pool',
  browse: 'the Spare Pool',
  requests: 'Tool Requests',
  inspect: 'Inspections',
  receipts: 'Receipts',
  users: 'User Management',
  import: 'Import',
  reports: 'Reports',
  models: 'Models',
  servicelog: 'the Service Log',
  inv_home: 'Stock Home',
  inv_catalog: 'the Catalog',
  inv_detail: 'that item',
  inv_receive: 'Receive stock',
  inv_use: 'Use stock',
  inv_adjust: 'Adjust count',
  inv_request: 'Request materials',
  inv_request_materials: 'Request materials',
  inv_ledger: 'the Ledger',
  inv_labels: 'Label printing',
  inv_offcatalog: 'Off-Catalog Review',
  requests_inbox: 'the Requests inbox',
  // #391, Session 78n. Load-bearing rather than cosmetic: canReachPage() is
  // `isKnownPage() && no fallback`, so a page missing from this map is
  // unreachable everywhere — the sidebar filter, the landing-page picker and
  // the /go/ resolver all read through it.
  my_requests: 'My requests',
  // #371 and #425, Session 79.6, gate decision D20 -- and this entry is the fix
  // for a defect that was DESIGNED OUT rather than found.
  //
  // A notification carries a page and a record id and NO INDICATION OF WHICH
  // TABLE that id belongs to. Without its own destination a transfer
  // notification falls through notificationNav.js's map to setSelectedItemId(),
  // which is the INVENTORY ITEM namespace -- so "transfer 3 has shipped" would
  // have opened inventory item 3. Silently, cleanly, plausibly, and both tables
  // number from 1.
  //
  // A separate destination makes the collision impossible rather than something
  // we remember. Acceptance line T13 runs it deliberately with a colliding id
  // present, because it is the line a casual tester ticks by accident.
  transfers: 'Transfers',
  production_queue: 'the To make queue',
  // #746, Session 79.22b.6.3 -- Houston's welders' page. One of the eleven places
  // gate 746's F4 names; weldingPageKey746.test.js holds all eleven to it.
  welding: 'Welding',
  purchasing_queue: 'the Purchasing Queue',
  purchasing_invoicing: 'Invoicing',
  purchasing_vendors: 'the Vendor Catalog',
  insights: 'Insights',
  company_settings: 'Company Settings',
  printer_registry: 'the Printer Registry',
  speak_up_admin: 'the Speak Up inbox',
  notifications: 'Notifications',
  notification_preferences: 'Notification preferences',
  profile: 'My Profile',
};

// Which module rail lights up. Deliberately NOT App.js's domainForPage(), which
// answers a different question — that one returns 'invoicing' as its own domain
// for the table-header accent, while Invoicing sits under the purchasing module
// in the sidebar. Two questions, two functions.
//
// inv_offcatalog is 'inventory' and that is not a slip: Off-catalog review is
// listed in the sidebar's INVENTORY section even though its work is purchasing's.
export function moduleForPage(pageKey) {
  if (['purchasing_queue', 'purchasing_vendors', 'purchasing_invoicing'].includes(pageKey)) return 'purchasing';
  if (pageKey === 'insights') return 'insights';
  // #371, Session 79.6. Transfers and the To make queue are inventory: they move
  // stock between branches and nothing about them is a purchase. Same rule as the
  // Topbar map -- the domain follows the reader's destination.
  // #746: Welding too -- it sits in the Inventory menu between Transfers and the
  // Ledger, and welded frames are stock on their way to To make.
  if (pageKey === 'transfers' || pageKey === 'production_queue' || pageKey === 'welding') return 'inventory';
  if (pageKey === 'requests_inbox' || pageKey === 'my_requests' || String(pageKey).startsWith('inv_')) return 'inventory';
  return 'tools';
}

// #167, Session 78j — the pages a person may choose to land on.
//
// Deliberately NOT derived from PAGE_LABELS. That map exists to finish the
// sentence "this link points to ___", so its entries read as fragments ("the
// Purchasing Queue", "that item") and it includes destinations nobody would
// choose as a front door -- inv_detail, notifications, a modal-ish screen like
// inv_adjust. A landing page is a place you start a day, which is a smaller and
// differently-worded set.
//
// The blurb is gate decision D9: a picker showing only page names asks someone
// to choose between words. Order is the order the picker renders in, most
// likely first.
export const LANDING_CANDIDATES = [
  { key: 'inv_home',            name: 'Stock Home',       blurb: 'Receive, use and adjust inventory' },
  { key: 'requests_inbox',      name: 'Requests inbox',   blurb: 'Approve and track material requests' },
  // #515 D33, Session 79.8 — "a destination like any other".
  //
  // The fabrication manager's front door. The wider navigation work Colby
  // designed — a role default plus up to four icons each pointed wherever the
  // person chooses — is its own issue and its own gate; burying it here would
  // hide it. What D33 asks for TODAY is that her landing page can be set for
  // her by name, and that needs this row and nothing else.
  //
  // The gate said it could already be done "with no build". It could not: the
  // page key existed for deep links but was in neither this list nor App.js's
  // route map, so a notification could name it and nobody could ever land on
  // it. One row and one route, found by trying it.
  { key: 'production_queue',    name: 'To make',          blurb: 'What this branch needs to build, orders first' },
  // #746, Session 79.22b.6.3 -- gate 746's copy table, word for word. Beside To
  // make because the two are one shop's front doors: the welders', and the
  // fabrication manager's. Offered only where landingChoicesFor() lets it.
  { key: 'welding',             name: 'Welding',          blurb: 'How many of each frame and stake to weld, late first' },
  { key: 'purchasing_queue',    name: 'Purchasing Queue', blurb: 'Source, price and place orders' },
  { key: 'purchasing_invoicing',name: 'Invoicing',        blurb: 'Match invoices to orders' },
  { key: 'insights',            name: 'Insights',         blurb: 'Spend, vendors and cycle times' },
  { key: 'inv_catalog',         name: 'Catalog',          blurb: 'Search every item Stede stocks' },
  { key: 'inv_ledger',          name: 'Ledger',           blurb: 'Every stock movement, filtered' },
  { key: 'fleet',               name: 'Fleet Inventory',  blurb: 'Every tool across D&R Signs' },
];

// Callout C — only pages the person's role can actually reach. Offering a
// landing page that then refuses to load is worse than not offering it, and it
// keeps the list short enough to scan in one look.
export function landingChoicesFor(auth) {
  return LANDING_CANDIDATES.filter(c => canReachPage(auth, c.key));
}

export function isKnownPage(pageKey) {
  return Object.prototype.hasOwnProperty.call(PAGE_LABELS, pageKey);
}

export function pageLabel(pageKey) {
  return PAGE_LABELS[pageKey] || 'that page';
}

// Where a person lands when they have nowhere better to be.
//
// #167, Session 78j — this is the per-user setting the seam was left for at
// 78h, and the comment that used to sit here said "at which point this function
// reads it and nothing else changes." That held: every call site still calls
// one function.
//
// Two layers, gate decision D1, which is what every system researched does.
// ServiceNow states the reason and it is exactly the September problem: role
// defaults exist so a new user is useful on day one without configuring
// anything, and forty Dallas onboards will not visit a settings page first.
//
//   1. The person's own choice, per platform (D3) -- desktop and phone are
//      stored separately, because the same person wants Invoicing at a desk
//      and Stock Home on a phone.
//   2. The role default, administered as data rather than code (D2).
//   3. 'fleet', which is where everyone lands today.
//
// Every candidate is filtered through canReachPage before it is returned, and
// that is load-bearing rather than defensive. A role can be changed by an
// administrator long after someone set a landing page, and callout C's rule is
// that offering a landing page which then refuses to load is worse than not
// offering one. This is also why the columns carry no CHECK constraint -- see
// the migration header. An unreachable value is inert here, not broken.
//
// `options.isMobile` decides which of the two personal values is read. A caller
// that does not know or does not care gets the desktop one, which is the safe
// default: it is the value a person is most likely to have set deliberately.
export function landingPageFor(auth, options = {}) {
  const { profile, roleDefaults, isMobile = false } = options;

  const personal = isMobile
    ? profile?.landing_page_mobile
    : profile?.landing_page_desktop;

  const roleDefault = roleDefaults?.[profile?.role];

  for (const candidate of [personal, roleDefault]) {
    if (!candidate) continue;
    // #746, Session 79.22b.6.3 -- a page whose answer is still on its way is
    // CHOSEN, not skipped: the page shows itself loading, and the rule applies
    // the moment the answer lands. Skipping it would send a welder whose opening
    // page is Welding to their role's default at every sign-in -- the failure
    // gate 746's F4 names.
    if (canReachPage(auth, candidate) || pageRulePending(auth, candidate)) return candidate;
  }
  return 'fleet';
}

// The role rules that decide whether a page key is reachable at all.
//
// Extracted from App.js's getPage(), which owned them inline and answered by
// silently substituting FleetPage — which is precisely the dead end gate
// decision E7 forbids for a deep link. getPage() now calls this too, so the
// substitution a person sees when they navigate and the explanation they get
// when they arrive from an email are the same rule rather than two copies.
//
// Returns null when the page is reachable, otherwise the page key to fall back
// to. The purchasing branch reads through the shared predicate instead of the
// four-term inline negation it replaced, per locked rule 8.
const EXECUTIVE_BLOCKED = ['users', 'import', 'models', 'servicelog'];
// #391 D10, Session 78n — `requests_inbox` joins this list. The Requests inbox
// is the branch's APPROVAL queue: its tabs are Needs approval and In flight, its
// framing is approval throughout, and a team_member cannot approve anything on
// it. Worse, `isBranchScopedManager` is false for that role, so load() applies
// no branch filter and they saw every pending and ordered request at EVERY
// branch. Not a data leak — canApprove() correctly gates every action — but a
// screen whose entire purpose is wrong for that reader.
//
// Removing it here closes all three doors at once, which is the point: this
// list feeds pageFallbackFor(), which feeds canReachPage(), which the sidebar
// filter, the landing-page picker and the /go/ deep-link resolver all read. The
// picker in particular was offering it to them described as "Approve and track
// material requests".
//
// The replacement destination is my_requests, which is why this line and #391's
// page ship together rather than in sequence — blocking the old screen before
// the new one exists would leave a requester with nowhere at all.
const TEAM_MEMBER_BLOCKED = ['spare', 'requests', 'servicelog', 'users', 'import', 'reports', 'models', 'receipts', 'requests_inbox'];
const PURCHASING_STAFF_ONLY = ['purchasing_queue', 'purchasing_vendors', 'inv_offcatalog', 'purchasing_invoicing', 'insights'];
// #811, Session 296 — Purchasing loses User Management (Colby's R4 at Session
// 293, gate item 4). The page offered them Edit and Deactivate beside everybody,
// and #798's guard refuses every one of those presses: Purchasing changes no
// record but its own. A link to it lands on their own queue, the way an
// executive's lands on Reports.
const PURCHASING_BLOCKED = ['users'];

export function pageFallbackFor(auth, pageKey) {
  const { isAdmin, isExecutive, isTeamMember, isPurchasing } = auth;
  if (pageKey === 'printer_registry' && !isAdmin) {
    return isExecutive ? 'reports' : 'fleet';
  }
  if (isExecutive && EXECUTIVE_BLOCKED.includes(pageKey)) return 'reports';
  if (isPurchasing && PURCHASING_BLOCKED.includes(pageKey)) return 'purchasing_queue';
  // #391 D10, Session 78n. The approval queue falls back to My requests rather
  // than to Fleet. Every other entry on the blocked list has no counterpart, so
  // the front door is the only honest answer for them; this one has an exact
  // counterpart, and sending a requester to Tools when they were reaching for
  // their own requests would be the dead end gate decision E7 forbids.
  if (isTeamMember && pageKey === 'requests_inbox') return 'my_requests';
  if (isTeamMember && TEAM_MEMBER_BLOCKED.includes(pageKey)) return 'fleet';
  if (!isStaffWithPurchasing(auth) && PURCHASING_STAFF_ONLY.includes(pageKey)) return 'fleet';
  // #746, Session 79.22b.6.3 (746 D5) -- THE FIRST RULE IN STEDE THAT READS THE
  // PERSON'S BRANCH. Every rule above reads the role alone. Welding is for every
  // branch that welds (Houston today) and for admins and executives at any
  // branch; anyone else is sent to Stock Home, which is where a link to it lands
  // them (gate 746, X1). canSeeWelding() reads auth.profile and
  // auth.weldingBranchIds, which is why every caller builds `auth` with
  // pageRuleAuth() below.
  //
  // While the sign-in's read of which branches weld is on its way this answers
  // "not reachable", so menus and choosers leave Welding out until it is known;
  // pageRulePending() tells the places that SEND somebody somewhere to wait.
  if (pageKey === 'welding' && !canSeeWelding(auth)) return 'inv_home';
  return null;
}

export function canReachPage(auth, pageKey) {
  return isKnownPage(pageKey) && pageFallbackFor(auth, pageKey) === null;
}

// #746, Session 79.22b.6.3 (gate 746 F4, as amended) -- THE ONE PLACE THE PAGE
// RULE'S INPUT IS BUILT. Until Welding, every rule above read the five role
// flags, and seven places built that object by hand: App.js five times (where a
// person lands, the page itself, the logo's way home, and the /go/ resolver
// twice), the menu, and My profile. Welding's rule also reads the person's
// branch and which branches weld, so a place still building the old object
// would decide without them -- and turn a welder away at that door. Pass
// useAuth()'s value through this, never an object literal:
// weldingPageKey746.test.js fails on a literal.
export function pageRuleAuth(auth) {
  const {
    isAdmin, isManager, isExecutive, isTeamMember, isPurchasing,
    profile, weldingBranchIds, weldingBranchesStatus,
  } = auth || {};
  return {
    isAdmin, isManager, isExecutive, isTeamMember, isPurchasing,
    profile, weldingBranchIds, weldingBranchesStatus,
  };
}

// #746 -- IS THIS PAGE'S ANSWER STILL ON ITS WAY? True only for Welding, for
// someone who is not an admin or an executive (they always see it), while the
// sign-in has not yet heard which branches weld -- 'loading', or a signed-in
// person whose read has not been started yet. The places that SEND a person
// somewhere -- their landing page, the page itself, the /go/ resolver -- wait on
// it rather than substituting. A menu or a chooser does not: leaving the item
// out for a moment is the safe direction (746 D5). A FAILED read is an answer,
// not a wait: the page is hidden until the retry lands (AuthContext.js).
export function pageRulePending(auth, pageKey) {
  if (pageKey !== 'welding') return false;
  if (auth?.isAdmin || auth?.isExecutive) return false;
  const status = auth?.weldingBranchesStatus;
  if (status === 'loading') return true;
  return status == null && auth?.profile != null;
}

// #391, Session 78n. Found by testing rather than by reading, which is the only
// reason it was found at all.
//
// Almost every fallback is a CONSOLATION: the reader cannot have the page they
// asked for and there is no counterpart, so they get the front door and the
// /go/ resolver names what it could not open (gate #126, decision E7). That is
// right, and it is why the resolver refuses rather than substituting.
//
// `requests_inbox` -> `my_requests` is the one fallback that is not a
// consolation. It is an EQUIVALENT: a team member clicking "your material
// request was approved" is sent to the page showing that very request. Running
// E7's refusal screen over it produces a message that is actively wrong — it
// tells someone their account lacks access at the exact moment the app is
// working as designed, about a notification Stede itself sent them.
//
// This matters beyond new notifications. D11 retargets the two call sites, but
// every approval and denial notification sent BEFORE this session still carries
// `requests_inbox`, and those rows do not rewrite themselves. Without this,
// every one of them is a dead end for the person it was sent to.
//
// Deliberately narrow: it returns a page only when that page is genuinely where
// the fallback already points and the reader can actually reach it, so it can
// never invent a destination the guard would refuse.
const PAGE_EQUIVALENTS = {
  requests_inbox: 'my_requests',
};

export function equivalentPageFor(auth, pageKey) {
  const target = PAGE_EQUIVALENTS[pageKey];
  if (!target) return null;
  if (pageFallbackFor(auth, pageKey) !== target) return null;
  return canReachPage(auth, target) ? target : null;
}

// ── #605, Session 79.21c — A LINK ID THAT SAYS WHICH TABLE IT CAME FROM ──────
//
// The Requests inbox holds BOTH material requests and inter-branch transfers,
// and its notification link id has always been a material_requests id. So a
// transfer could not be addressed there at all: transfer 75 and request 75 are
// different things wearing one number, and a bare 75 silently opens the wrong
// record with every number on screen looking right. That is why the approval
// bell shipped with a null id and opened a LIST.
//
// Colby's ruling at 79.21b: make the app match acceptance line K1 — the bell
// opens the transfer it names. Then he narrowed it himself, and the narrowing
// is the load-bearing part: link by the DATABASE ROW ID, not the transfer
// number, because `transfer_number` IS NULL until the supplying branch
// confirms. Proven in the data — transfers 75 and 76 both sat at NULL while
// waiting on exactly the approval this bell is about.
//
// `notifications.link_id` is TEXT, so the prefix costs no migration.
//
// A PREFIXED ID IS NEVER SILENTLY TREATED AS A NUMBER. Anything reading link_id
// must call parseNamespacedLinkId() first: parseInt('transfer:75', 10) is NaN,
// and handing NaN to a record lookup is the same class of silent wrongness the
// prefix exists to prevent.
export const TRANSFER_LINK_PREFIX = 'transfer:';

export function transferLinkId(id) {
  return id == null ? null : `${TRANSFER_LINK_PREFIX}${id}`;
}

// Returns { kind, id } — kind 'transfer' for a prefixed id, 'plain' for a bare
// numeric one, and null when there is nothing usable. `id` is always a number
// or null, never NaN.
export function parseNamespacedLinkId(linkId) {
  if (linkId == null || linkId === '') return null;
  const raw = String(linkId);
  if (raw.startsWith(TRANSFER_LINK_PREFIX)) {
    const n = parseInt(raw.slice(TRANSFER_LINK_PREFIX.length), 10);
    return Number.isFinite(n) ? { kind: 'transfer', id: n } : null;
  }
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? { kind: 'plain', id: n } : null;
}

